import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { newSiteValidationSchema } from "../../../utils";
import { Modal, InputField, SiteAutocomplete, Button, Loader } from "../../../components";
import { useLanguage } from "../../../hooks";
import { usePublicCountries } from "../../../hooks/useCountries";
import { useCreateSite } from "../../../hooks/useSites";
import { queryKeys } from "../../../hooks/queryKeys";
import { useNotification, useUser } from "../../../context";
import { SiteResponse } from "../../../types/site.type";

type FormValues = {
  internalCode: string;
  name: string;
  comment?: string;
  country: string;
};

type InlineCreateSiteModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCreated: (site: SiteResponse) => void;
  onUseOnceAddress: (payload: {
    address: string;
    country: string | null;
  }) => void;
};

const InlineCreateSiteModal = ({
  open,
  setOpen,
  onCreated,
  onUseOnceAddress,
}: InlineCreateSiteModalProps) => {
  const { user } = useUser();
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const langEs = useLanguage();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: yupResolver(newSiteValidationSchema) });

  const [address, setAddress] = useState<any>({});
  const [comment, setComment] = useState("");
  const [saveToMySites, setSaveToMySites] = useState(false);

  const { data: countries, isLoading: isLoadingCountries } =
    usePublicCountries();

  // When the Places autocomplete picks a place, lift the chosen country out of
  // its address_components and pre-select the corresponding option in our
  // country dropdown. The buyer can still override manually if Places picked
  // wrong (or returned an unsupported country).
  const handlePlaceSelected = (place: any) => {
    setAddress(place);
    const components = place?.address_components ?? [];
    const countryComponent = components.find((c: any) =>
      c?.types?.includes("country")
    );
    const long = countryComponent?.long_name as string | undefined;
    if (!long || !countries?.length) return;
    const normalized = long.trim().toLowerCase();
    const matched = countries.find(
      (c) =>
        c.name?.trim().toLowerCase() === normalized ||
        c.nameEn?.trim().toLowerCase() === normalized
    );
    if (matched?.id) {
      setValue("country", matched.id, { shouldValidate: true });
    }
  };

  const { mutate: createSite, isPending } = useCreateSite(
    () => undefined,
    () => openNotification(t("newMaterialForm.error"))
  );

  const close = () => {
    reset();
    setAddress({});
    setComment("");
    setSaveToMySites(false);
    setOpen(false);
  };

  const onSubmit = (data: FormValues) => {
    if (!address?.formatted_address) {
      setAddress("sites.errorPlace");
      return;
    }
    if (!saveToMySites) {
      // Use-once: emit just the typed address back to the parent without
      // persisting a Site. The address travels with this RFQ only.
      onUseOnceAddress({
        address: address?.formatted_address ?? "",
        country: data.country ?? null,
      });
      close();
      return;
    }
    createSite(
      {
        ...data,
        company: { id: user?.company?.id },
        address: address?.formatted_address,
        googlePlaceId: address?.place_id,
        country: { id: data.country },
        comment,
      },
      {
        onSuccess: (site: SiteResponse) => {
          // The endpoint returns a { message } body (HTTP 200) when the
          // internal code already exists; only proceed on a real site.
          if (!site?.id) {
            openNotification(t("sites.codeExists"));
            return;
          }
          openNotification(t("sites.success"));
          queryClient.invalidateQueries({ queryKey: [queryKeys.SITES] });
          onCreated(site);
          close();
        },
      }
    );
  };

  return (
    <Modal title={t("sites.add")} open={open} setOpen={setOpen}>
      {isLoadingCountries ? (
        <Loader />
      ) : (
        <div className="space-y-4">
          <InputField
            id="internalCode"
            name="internalCode"
            type="text"
            label={t("sites.code")}
            errorName={errors?.internalCode}
            errorMessage={errors?.internalCode?.message}
            required
            register={register}
          />
          <InputField
            id="name"
            name="name"
            type="text"
            label={t("sites.name")}
            errorName={errors?.name}
            errorMessage={errors?.name?.message}
            required
            register={register}
          />
          <div>
            <SiteAutocomplete setAddress={handlePlaceSelected} />
            {address === "sites.errorPlace" && (
              <span className="text-xs text-red-500">{t(address)}</span>
            )}
          </div>
          <div>
            <label
              htmlFor="country"
              className="uppercase block text-xs font-medium text-gray-700"
            >
              {t("sites.country")}
              <span className="text-sm text-red-600"> *</span>
            </label>
            <select
              {...register("country", { required: true })}
              id="country"
              name="country"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-xs rounded"
              defaultValue=""
            >
              <option value="">{t("validations.country")}</option>
              {countries?.map((country) => (
                <option key={country.id} value={country.id}>
                  {langEs ? country.name : country.nameEn}
                </option>
              ))}
            </select>
            {errors?.country && (
              <span className="text-xs text-red-500">
                {errors?.country?.message}
              </span>
            )}
          </div>
          <div>
            <label
              htmlFor="comment"
              className="uppercase block text-xs font-medium text-gray-700 mb-1"
            >
              {t("sites.comment")}
            </label>
            <input
              type="text"
              value={comment}
              className="p-2 border-gray-300 rounded w-full text-xs"
              onChange={(e) => {
                if (e.target.value.length === 255) return;
                setComment(e.target.value);
              }}
              placeholder={t("sites.comment")}
            />
            <span className="text-xs">{comment.length} / 255</span>
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs text-gray-700 mt-3">
              <input
                type="checkbox"
                checked={saveToMySites}
                onChange={(e) => setSaveToMySites(e.target.checked)}
              />
              {t("orderByRequest.saveToMySitesLabel")}
            </label>
            <p className="text-xxs text-gray-500 mt-1">
              {t("orderByRequest.saveToMySitesHint")}
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="cancel" onClick={close}>
              {t("cta.cancel")}
            </Button>
            <Button disabled={isPending} onClick={handleSubmit(onSubmit)}>
              {t("cta.add")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InlineCreateSiteModal;
